import React, { useEffect, useState } from 'react';
import settingsService from '../../services/settingsService';

/**
 * Settings → Notifications: opt-in email/SMS alerts when a new chat arrives via
 * the widget (ADR-0001). Both channels are OFF until the user enables them and
 * supplies a destination. SMS controls are disabled when the server has no SMS
 * provider configured.
 */
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
      checked ? 'bg-blue-600' : 'bg-gray-600'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const NotificationsTab = ({ onNotify }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState({
    email_enabled: false,
    alert_email: '',
    sms_enabled: false,
    alert_phone: '',
    daily_cap: 0,
    min_interval_seconds: 0,
    email_available: false,
    sms_available: false,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await settingsService.getNotificationSettings();
        if (alive) setS((prev) => ({ ...prev, ...data, alert_email: data.alert_email || '', alert_phone: data.alert_phone || '' }));
      } catch (e) {
        onNotify?.('Could not load notification settings', 'error');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [onNotify]);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        email_enabled: s.email_enabled,
        alert_email: s.alert_email.trim(),
        sms_enabled: s.sms_enabled,
        alert_phone: s.alert_phone.trim(),
        daily_cap: Number(s.daily_cap) || 0,
        min_interval_seconds: Number(s.min_interval_seconds) || 0,
      };
      const data = await settingsService.updateNotificationSettings(payload);
      setS((prev) => ({ ...prev, ...data, alert_email: data.alert_email || '', alert_phone: data.alert_phone || '' }));
      onNotify?.('Notification settings saved');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not save settings';
      onNotify?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'block w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm disabled:opacity-50';

  if (loading) return <div className="text-gray-400">Loading notification settings…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">New-chat alerts</h2>
        <p className="text-gray-400 mt-1 text-sm">
          Get notified by email or SMS when a visitor starts a new chat through your widget.
          Both are off by default.
        </p>
      </div>

      {/* Email channel */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Email alerts</h3>
            <p className="text-sm text-gray-400">Send an email when a new chat comes in.</p>
          </div>
          <Toggle
            checked={s.email_enabled}
            disabled={!s.email_available}
            onChange={(v) => set('email_enabled', v)}
          />
        </div>
        {!s.email_available && (
          <p className="mt-2 text-xs text-warn">Email isn’t configured on the server yet (set MAIL_PROVIDER).</p>
        )}
        {s.email_enabled && (
          <div className="mt-4">
            <label htmlFor="alert_email" className="block text-sm text-gray-300 mb-1">Send alerts to</label>
            <input id="alert_email" type="email" className={inputCls} placeholder="alerts@yourcompany.com"
              value={s.alert_email} onChange={(e) => set('alert_email', e.target.value)} />
          </div>
        )}
      </div>

      {/* SMS channel */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">SMS alerts</h3>
            <p className="text-sm text-gray-400">Text a phone number when a new chat comes in (carrier charges apply).</p>
          </div>
          <Toggle
            checked={s.sms_enabled}
            disabled={!s.sms_available}
            onChange={(v) => set('sms_enabled', v)}
          />
        </div>
        {!s.sms_available && (
          <p className="mt-2 text-xs text-warn">SMS isn’t configured on the server yet (set SMS_PROVIDER + BREVO_API_KEY).</p>
        )}
        {s.sms_enabled && (
          <div className="mt-4">
            <label htmlFor="alert_phone" className="block text-sm text-gray-300 mb-1">Send alerts to (E.164)</label>
            <input id="alert_phone" type="tel" className={inputCls} placeholder="+14155550100"
              value={s.alert_phone} onChange={(e) => set('alert_phone', e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">Include country code, e.g. +1 for US.</p>
          </div>
        )}
      </div>

      {/* Guards */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="daily_cap" className="block text-sm text-gray-300 mb-1">Daily cap (0 = unlimited)</label>
          <input id="daily_cap" type="number" min="0" className={inputCls}
            value={s.daily_cap} onChange={(e) => set('daily_cap', e.target.value)} />
          <p className="mt-1 text-xs text-gray-500">Max alerts per day — protects against SMS cost spikes.</p>
        </div>
        <div>
          <label htmlFor="min_interval" className="block text-sm text-gray-300 mb-1">Min seconds between alerts</label>
          <input id="min_interval" type="number" min="0" className={inputCls}
            value={s.min_interval_seconds} onChange={(e) => set('min_interval_seconds', e.target.value)} />
          <p className="mt-1 text-xs text-gray-500">Throttle bursts (e.g. 60).</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={saving}
          className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default NotificationsTab;
